import { ContentLayout, Alert, Box, Header, Link } from '@cloudscape-design/components';
import { QuickSightTabbedView } from '@amzn/gbsa-quick-sight-embed-ui';
import { Component, ReactNode } from 'react';
import defaultDashboardConfig from '../data/quicksight-config';
import { DashboardConfig } from '../data/quicksight-config';
import { useUserPreferences } from '../store/userPreferences/useUserPreferences';
import { useEffect } from 'react';
import useUserProfile from '../hooks/useUserProfile';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component to catch and display errors from QuickSight dashboard
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('QuickSight Dashboard Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box padding="xxl">
          <Alert type="error" header="Unable to load dashboard">
            {this.state.error?.message ||
              'An error occurred while loading the QuickSight dashboard. Please try refreshing the page.'}
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}

interface ReportsPageProps {
  config?: DashboardConfig;
}

/**
 * Reports page component that displays QuickSight dashboards
 */
const ReportsPage = ({ config = defaultDashboardConfig }: ReportsPageProps) => {
  const { userPreferences, loadUserPreferences } = useUserPreferences();
  const { getUserProgramId, getUserId } = useUserProfile();

  // Load user preferences to get their selected Business Unit
  useEffect(() => {
    loadUserPreferences({ programId: getUserProgramId(), userId: getUserId() });
  }, [loadUserPreferences, getUserProgramId, getUserId]);

  // Extract Business Unit ID from user preferences
  // This will be used as the QuickSight parameter value (e.g., "AMZL", "AWS")
  const businessUnitId = userPreferences?.profileSettings?.businessUnit;

  // Build parameter values object
  // Only include BusinessUnit if user has one selected
  const parameterValues = businessUnitId ? { BusinessUnit: businessUnitId } : undefined;

  const defaultDashboard =
    config.dashboards.find((d) => d.defaultTab) ?? config.dashboards[0];

  if (!defaultDashboard) {
    return (
      <ContentLayout>
        <Box padding="xxl">
          <Alert type="warning" header="No dashboards configured">
            There are no dashboards available. Please contact your administrator.
          </Alert>
        </Box>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      header={
        <Header variant="h3">
          <Link
            href={defaultDashboard.embedUrl}
            external
            fontSize="heading-m"
            ariaLabel="Open dashboard in QuickSight"
          >
            Gravion Audit Dashboard
          </Link>
        </Header>
      }
    >
      <ErrorBoundary>
        <QuickSightTabbedView
          dashboards={config.dashboards}
          parameterValues={parameterValues}
          className="hide-tabs"
        />
      </ErrorBoundary>
    </ContentLayout>
  );
};

export default ReportsPage;
